import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/admin/ProtectedRoute';
import { useTranslation } from 'react-i18next';
import './i18n';
import { applySayfaOverrides } from './utils/sayfaIcerikleri';
import { initGA, trackPageView } from './utils/analytics';
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import ScrollToTop from './components/Common/ScrollToTop';
import Home from './pages/Home';
import PageTemplate from './components/Common/PageTemplate';

// Sayfa yüklenirken gösterilecek fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-hacettepe-blue" />
  </div>
);

// Admin sayfaları
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const AdminResetPassword = lazy(() => import('./pages/AdminResetPassword'));

// Genel sayfalar
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const News = lazy(() => import('./pages/NewsPage'));
const HaberDetay = lazy(() => import('./pages/HaberDetay'));
const AkademikFaaliyet = lazy(() => import('./pages/AkademikFaaliyet'));
const Publications = lazy(() => import('./pages/PublicationsPage'));
const Announcements = lazy(() => import('./pages/Announcements'));
const AnnouncementDetail = lazy(() => import('./pages/AnnouncementDetail'));
const Etkinlikler = lazy(() => import('./pages/Etkinlikler'));
const BirimFaaliyetleri = lazy(() => import('./pages/BirimFaaliyetleri'));
const Videogaleri = lazy(() => import('./pages/Videogaleri'));
const EngelsizYasam = lazy(() => import('./pages/EngelsizYasam'));
const AlumniPage = lazy(() => import('./pages/AlumniPage'));
const DynamicPage = lazy(() => import('./pages/DynamicPage'));
const OgrenciTopluluklari = lazy(() => import('./pages/OgrenciTopluluklari'));
const StajPage = lazy(() => import('./pages/Staj'));
const SosyalSorumlulukProjeleri = lazy(() => import('./pages/SosyalSorumlulukProjeleri'));

// Statik haber sayfaları
const AnitkabirZiyareti2025 = lazy(() => import('./pages/haberler/2024-2025-akademik-yili-fen-fakultesi-mezunlari-anitkabir-ziyareti'));
const ErasmusCagliari = lazy(() => import('./pages/haberler/hacettepe-universitesi-erasmus-programi-cagliari'));
const MezuniyetToreni2025 = lazy(() => import('./pages/haberler/2025-yili-mezuniyet-toreni'));
const Tubitak1001Sonuclari = lazy(() => import('./pages/haberler/Tubitak1001Sonuclari'));
const ProfAsliOzkokFaoProje = lazy(() => import('./pages/haberler/prof-dr-asli-ozkok-saban-fao-projesinde-ulusal-uzman-egitimci-secildi'));
const BeyazOnlukToreni = lazy(() => import('./pages/haberler/hacettepe-universitesi-kimya-bolumu-3-beyaz-onluk-giyme-toreni-gerceklestirildi'));
const AsaInternationalDataQuest2025 = lazy(() => import('./pages/haberler/asa-international-data-quest-2025-cifte-turkiye-birinciligi'));
const DavutAksutUSMZiyareti = lazy(() => import('./pages/haberler/davut-aksut-universiti-sains-malaysia-ziyareti'));
const BeytepGoleteleriAcigolKorumaProjesi = lazy(() => import('./pages/haberler/beytepe-goletleri-acigol-dislisazancigi-koruma-projesi'));
const BatiAsyaTatlisulariKapsamliKitap = lazy(() => import('./pages/haberler/bati-asya-tatlisubaliklari-icin-kapsamli-basvuru-kitabi-yayimlandi'));
const OgretimUyemizZoologyDergisiBasEditor = lazy(() => import('./pages/haberler/ogretim-uyemiz-zoology-in-the-middle-east-dergisine-bas-editor-secilmistir'));
const OgretimUyemizIUCNKongresi2025 = lazy(() => import('./pages/haberler/ogretim-uyemiz-iucn-dunya-doga-koruma-kongresi-2025e-katildi'));
const FenVeMuhendislikOrtakProje = lazy(() => import('./pages/haberler/fen-fakultesi-ve-muhendislik-fakultesinden-ortak-proje'));
const TurkiyeTatlisulariEnvanterKoruma = lazy(() => import('./pages/haberler/turkiye-tatlisubaliklari-guncel-envanteri-ve-koruma-durumu-yayinlandi'));
const FedekAkreditasyonDegerlendirmeSureci = lazy(() => import('./pages/haberler/fakultemizde-fedek-akreditasyon-degerlendirme-sureci-gerceklestirildi'));
const KadriIlkerBerktav = lazy(() => import('./pages/haberler/genel-seminer-kadri-ilker-berktav'));
const SinanErgen = lazy(() => import('./pages/haberler/genel-seminer-sinan-ergen'));
const DunyaMatematikGunu = lazy(() => import('./pages/haberler/dunya-matematik-gunu-ilham-veren-bulusma'));
const TamasLaszlo = lazy(() => import('./pages/haberler/genel-seminer-tamas-laszlo'));
const SalihaKivanc = lazy(() => import('./pages/haberler/genel-seminer-saliha-kivanc'));

// Kurumsal Sayfalar
const History = lazy(() => import('./pages/Institutional/History'));
const MissionVision = lazy(() => import('./pages/Institutional/MissionVision'));
const FEDEK = lazy(() => import('./pages/Institutional/FEDEK'));
const StrategicPlan = lazy(() => import('./pages/Institutional/StrategicPlan'));
const FormerDeans = lazy(() => import('./pages/Institutional/FormerDeans'));

// Yönetim Sayfaları
const Dean = lazy(() => import('./pages/Management/Dean'));
const ViceDeans = lazy(() => import('./pages/Management/ViceDeans'));
const FacultyBoard = lazy(() => import('./pages/Management/FacultyBoard'));
const ManagementBoard = lazy(() => import('./pages/Management/ManagementBoard'));
const FacultySecretaries = lazy(() => import('./pages/Management/FacultySecretaries'));
const FakulteSekreteri = lazy(() => import('./pages/Management/FakulteSekreteri'));
const ManagementScheme = lazy(() => import('./pages/Management/ManagementScheme'));
const QualityStrategy = lazy(() => import('./pages/Management/QualityStrategy'));
const AcademicQualityCommission = lazy(() => import('./pages/Management/AcademicQualityCommission'));
const ScholarshipCommission = lazy(() => import('./pages/Management/ScholarshipCommission'));
const PromotionCeremonyCommission = lazy(() => import('./pages/Management/PromotionCeremonyCommission'));
const PublicationCommission = lazy(() => import('./pages/Management/PublicationCommission'));
const NonFormalLearningCommission = lazy(() => import('./pages/Management/NonFormalLearningCommission'));
const BirimDanismaKurulu = lazy(() => import('./pages/Management/BirimDanismaKurulu'));

// Personel Sayfaları
const Faculty = lazy(() => import('./pages/Staff/Faculty'));
const TeachingStaff = lazy(() => import('./pages/Staff/TeachingStaff'));
const ResearchStaff = lazy(() => import('./pages/Staff/ResearchStaff'));
const RetiredStaff = lazy(() => import('./pages/Staff/RetiredStaff'));
const FacultyMembers = lazy(() => import('./pages/Staff/FacultyMembers'));
const LecturingStaff = lazy(() => import('./pages/Staff/LecturingStaff'));
const ResearchAssistants = lazy(() => import('./pages/Staff/ResearchAssistants'));
const AdministrativeStaff = lazy(() => import('./pages/Staff/AdministrativeStaff'));
const RetiredFaculty = lazy(() => import('./pages/Staff/RetiredFaculty'));

// Belge Sayfaları
const AcademicStaff = lazy(() => import('./pages/Documents/AcademicStaff'));
const Students = lazy(() => import('./pages/Documents/Students'));
const Laws = lazy(() => import('./pages/Documents/Laws'));

// Karar Sayfaları
const FacultyBoardDecisions = lazy(() => import('./pages/Decisions/FacultyBoardDecisions'));
const FacultyManagementBoardDecisions = lazy(() => import('./pages/Decisions/FacultyManagementBoardDecisions'));
const FacultyBoardYearPage = lazy(() => import('./pages/Decisions/FacultyBoardYearPage'));
const FacultyManagementBoardYearPage = lazy(() => import('./pages/Decisions/FacultyManagementBoardYearPage'));

// Araştırma Sayfaları
const ResearchLabs = lazy(() => import('./pages/Research/ResearchLabs'));
const BooksAndJournals = lazy(() => import('./pages/Research/BooksAndJournals'));
const DergilerPage = lazy(() => import('./pages/Research/Dergiler'));
const Museums = lazy(() => import('./pages/Research/Museums'));
const DevamEdenProjeler = lazy(() => import('./pages/Research/DevamEdenProjeler'));
const DisDestekliProjeler = lazy(() => import('./pages/Research/DisDestekliProjeler'));
const BAPDestekliProjeler = lazy(() => import('./pages/Research/BAPDestekliProjeler'));

// Bölüm Sayfaları
const Biology = lazy(() => import('./pages/Departments/Biology'));
const BiologyFaculty = lazy(() => import('./pages/Departments/BiologyFaculty'));
const Mathematics = lazy(() => import('./pages/Departments/Mathematics'));
const Statistics = lazy(() => import('./pages/Departments/Statistics'));
const ActuarialScience = lazy(() => import('./pages/Departments/ActuarialScience'));
const KimyaBolumuSanalTur = lazy(() => import('./pages/Departments/KimyaBolumuSanalTur'));

// Mezun Sayfaları
const Graduates = lazy(() => import('./pages/Graduates/Graduates'));
const AlbumListPage = lazy(() => import('./pages/AlbumListPage'));
const AlbumDetailPage = lazy(() => import('./pages/AlbumDetailPage'));

// Programlar
const UndergraduateProgramsPage = lazy(() => import('./pages/Programs/Undergraduate'));
const ErasmusPage = lazy(() => import('./pages/Programs/Erasmus'));
const ErasmusOgrenciDeneyimleri = lazy(() => import('./pages/Programs/ErasmusOgrenciDeneyimleri'));
const YandalPage = lazy(() => import('./pages/Programs/Yandal'));
const CiftAnadalPage = lazy(() => import('./pages/Programs/CiftAnadal'));
const YuksekLisansPage = lazy(() => import('./pages/Programs/YuksekLisans'));
const DoktoraPage = lazy(() => import('./pages/Programs/Doktora'));
const UluslararasiOgrenciler = lazy(() => import('./pages/Student/UluslararasiOgrenciler'));
const StudentClubs = lazy(() => import('./pages/StudentClubs/StudentClubs'));

function App() {
    useEffect(() => {
        applySayfaOverrides();
    }, []);

    return (
        <Router>
            <AppRouter />
        </Router>
    );
}

function AppRouter() {
    const location = useLocation();

    if (location.pathname.startsWith('/admin')) {
        return (
            <Suspense fallback={<PageLoader />}>
                <Routes>
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                    <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
                    <Route path="/admin/*" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
                </Routes>
            </Suspense>
        );
    }

    return <AppContent />;
}

function LanguageWrapper({ children }) {
    const location = useLocation();
    const { i18n } = useTranslation();

    // İlk mount'ta localStorage temizle
    useEffect(() => {
        try {
            localStorage.clear();
        } catch (e) {}
    }, []); // Sadece ilk renderda

    // URL değiştiğinde dili güncelle
    useEffect(() => {
        const isEnglish = location.pathname.startsWith('/en/') || location.pathname === '/en';
        const targetLang = isEnglish ? 'en' : 'tr';

        // Sadece farklıysa değiştir
        if (i18n.language !== targetLang) {
            i18n.changeLanguage(targetLang);
        }

        document.documentElement.lang = targetLang;
    }, [location.pathname]); // i18n'i dependency'den çıkar

    return <>{children}</>;
}

function AppContent() {
    const location = useLocation();

    // Google Analytics sayfa takibi
    useEffect(() => {
        // İlk yükleme için GA'yi başlat
        if (location.pathname === '/' || location.pathname === '/en') {
            initGA();
        }

        // Her sayfa değişiminde track et
        const pageTitle = document.title || 'Hacettepe Üniversitesi Fen Fakültesi';
        trackPageView(location.pathname + location.search, pageTitle);
    }, [location]);

    return (
        <LanguageWrapper>
            <ScrollToTop />
            <div className="flex flex-col min-h-screen flow-bg">
                <Navbar />
                <main className="flex-grow">
                    <Suspense fallback={<PageLoader />}>
                    <Routes>
                    {/* Turkish routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/hakkimizda" element={<About />} />
                    <Route path="/iletisim" element={<Contact />} />

                    {/* English routes */}
                    <Route path="/en" element={<Home />} />
                    <Route path="/en/hakkimizda" element={<About />} />
                    <Route path="/en/iletisim" element={<Contact />} />
                    
                    <Route path="/haberler" element={<News />} />
                    <Route path="/en/haberler" element={<News />} />
                    <Route path="/haberler/2024-2025-akademik-yili-fen-fakultesi-mezunlari-anitkabir-ziyareti" element={<AnitkabirZiyareti2025 />} />
                    <Route path="/haberler/hacettepe-universitesi-erasmus-programi-cagliari" element={<ErasmusCagliari />} />
                    <Route path="/haberler/2025-yili-mezuniyet-toreni" element={<MezuniyetToreni2025 />} />
                    <Route path="/haberler/tubitak-1001-programi-2025-yili-1-donem-degerlendirme-sonuclari-aciklandi" element={<Tubitak1001Sonuclari />} />
                    <Route path="/haberler/prof-dr-asli-ozkok-saban-fao-projesinde-ulusal-uzman-egitimci-secildi" element={<ProfAsliOzkokFaoProje />} />
                    <Route path="/haberler/hacettepe-universitesi-kimya-bolumu-3-beyaz-onluk-giyme-toreni-gerceklestirildi" element={<BeyazOnlukToreni />} />
                    <Route path="/haberler/asa-international-data-quest-2025-cifte-turkiye-birinciligi" element={<AsaInternationalDataQuest2025 />} />
                    <Route path="/haberler/davut-aksut-universiti-sains-malaysia-ziyareti" element={<DavutAksutUSMZiyareti />} />
                    <Route path="/haberler/beytepe-goletleri-acigol-dislisazancigi-koruma-projesi" element={<BeytepGoleteleriAcigolKorumaProjesi />} />
                    <Route path="/haberler/bati-asya-tatlisubaliklari-icin-kapsamli-basvuru-kitabi-yayimlandi" element={<BatiAsyaTatlisulariKapsamliKitap />} />
                    <Route path="/haberler/ogretim-uyemiz-zoology-in-the-middle-east-dergisine-bas-editor-secilmistir" element={<OgretimUyemizZoologyDergisiBasEditor />} />
                    <Route path="/haberler/ogretim-uyemiz-iucn-dunya-doga-koruma-kongresi-2025e-katildi" element={<OgretimUyemizIUCNKongresi2025 />} />
                    <Route path="/haberler/fen-fakultesi-ve-muhendislik-fakultesinden-ortak-proje" element={<FenVeMuhendislikOrtakProje />} />
                    <Route path="/haberler/turkiye-tatlisubaliklari-guncel-envanteri-ve-koruma-durumu-yayinlandi" element={<TurkiyeTatlisulariEnvanterKoruma />} />
                    <Route path="/haberler/fakultemizde-fedek-akreditasyon-degerlendirme-sureci-gerceklestirildi" element={<FedekAkreditasyonDegerlendirmeSureci />} />
                    <Route path="/haberler/genel-seminer-kadri-ilker-berktav" element={<KadriIlkerBerktav />} />
                    <Route path="/haberler/genel-seminer-sinan-ergen" element={<SinanErgen />} />
                    <Route path="/haberler/dunya-matematik-gunu-ilham-veren-bulusma" element={<DunyaMatematikGunu />} />
                    <Route path="/haberler/genel-seminer-tamas-laszlo" element={<TamasLaszlo />} />
                    <Route path="/haberler/genel-seminer-saliha-kivanc" element={<SalihaKivanc />} />
                    <Route path="/haberler/:id" element={<HaberDetay />} />
                    <Route path="/en/haberler/:id" element={<HaberDetay />} />
                    <Route path="/etkinlikler" element={<Etkinlikler />} />
                    <Route path="/en/etkinlikler" element={<Etkinlikler />} />
                    <Route path="/yayinlar" element={<Publications />} />
                    <Route path="/en/yayinlar" element={<Publications />} />
                    <Route path="/duyurular" element={<Announcements />} />
                    <Route path="/en/duyurular" element={<Announcements />} />
                    <Route path="/duyurular/:id" element={<AnnouncementDetail />} />
                    <Route path="/en/duyurular/:id" element={<AnnouncementDetail />} />
                    <Route path="/galeri" element={<AlbumListPage />} />
                    <Route path="/en/galeri" element={<AlbumListPage />} />
                    <Route path="/albums" element={<AlbumListPage />} />
                    <Route path="/en/albums" element={<AlbumListPage />} />
                    <Route path="/albums/:albumId" element={<AlbumDetailPage />} />
                    <Route path="/en/albums/:albumId" element={<AlbumDetailPage />} />
                    <Route path="/videogaleri" element={<Videogaleri />} />
                    <Route path="/en/videogaleri" element={<Videogaleri />} />

                    <Route path="/kurumsal/tarihce" element={<History />} />
                    <Route path="/en/kurumsal/tarihce" element={<History />} />
                    <Route path="/kurumsal/misyon-vizyon" element={<MissionVision />} />
                    <Route path="/en/kurumsal/misyon-vizyon" element={<MissionVision />} />
                    <Route path="/kurumsal/fedek" element={<FEDEK />} />
                    <Route path="/en/kurumsal/fedek" element={<FEDEK />} />
                    <Route path="/kurumsal/stratejik-plan" element={<StrategicPlan />} />
                    <Route path="/en/kurumsal/stratejik-plan" element={<StrategicPlan />} />
                    <Route path="/kurumsal/eski-dekanlar" element={<FormerDeans />} />
                    <Route path="/en/kurumsal/eski-dekanlar" element={<FormerDeans />} />
                    <Route path="/engelsiz-yasam" element={<EngelsizYasam />} />
                    <Route path="/en/engelsiz-yasam" element={<EngelsizYasam />} />

                    <Route path="/yonetim/dekan" element={<Dean />} />
                    <Route path="/en/yonetim/dekan" element={<Dean />} />
                    <Route path="/yonetim/dekan-yardimcilari" element={<ViceDeans />} />
                    <Route path="/en/yonetim/dekan-yardimcilari" element={<ViceDeans />} />
                    <Route path="/yonetim/fakulte-kurulu" element={<FacultyBoard />} />
                    <Route path="/en/yonetim/fakulte-kurulu" element={<FacultyBoard />} />
                    <Route path="/yonetim/yonetim-kurulu" element={<ManagementBoard />} />
                    <Route path="/en/yonetim/yonetim-kurulu" element={<ManagementBoard />} />
                    <Route path="/yonetim/fakulte-sekreterleri" element={<FacultySecretaries />} />
                    <Route path="/en/yonetim/fakulte-sekreterleri" element={<FacultySecretaries />} />
                    <Route path="/yonetim/fakulte-sekreteri" element={<FakulteSekreteri />} />
                    <Route path="/en/yonetim/fakulte-sekreteri" element={<FakulteSekreteri />} />
                    <Route path="/yonetim/yonetim-semasi" element={<ManagementScheme />} />
                    <Route path="/en/yonetim/yonetim-semasi" element={<ManagementScheme />} />

                    <Route path="/kurul-komisyonlar/birim-danisma-kurulu" element={<BirimDanismaKurulu />} />
                    <Route path="/en/kurul-komisyonlar/birim-danisma-kurulu" element={<BirimDanismaKurulu />} />
                    <Route path="/kurul-komisyonlar/kalite-strateji" element={<QualityStrategy />} />
                    <Route path="/en/kurul-komisyonlar/kalite-strateji" element={<QualityStrategy />} />
                    <Route path="/kurul-komisyonlar/akademik-birim-kalite" element={<AcademicQualityCommission />} />
                    <Route path="/en/kurul-komisyonlar/akademik-birim-kalite" element={<AcademicQualityCommission />} />
                    <Route path="/kurul-komisyonlar/burs-komisyonu" element={<ScholarshipCommission />} />
                    <Route path="/en/kurul-komisyonlar/burs-komisyonu" element={<ScholarshipCommission />} />
                    <Route path="/kurul-komisyonlar/tanitim-toren" element={<PromotionCeremonyCommission />} />
                    <Route path="/en/kurul-komisyonlar/tanitim-toren" element={<PromotionCeremonyCommission />} />
                    <Route path="/kurul-komisyonlar/yayin" element={<PublicationCommission />} />
                    <Route path="/en/kurul-komisyonlar/yayin" element={<PublicationCommission />} />
                    <Route path="/kurul-komisyonlar/program-disi-ogrenme" element={<NonFormalLearningCommission />} />
                    <Route path="/en/kurul-komisyonlar/program-disi-ogrenme" element={<NonFormalLearningCommission />} />

                    <Route path="/personel/akademisyen" element={<Faculty />} />
                    <Route path="/en/personel/akademisyen" element={<Faculty />} />
                    <Route path="/personel/ogretim-gorevlisi" element={<TeachingStaff />} />
                    <Route path="/en/personel/ogretim-gorevlisi" element={<TeachingStaff />} />
                    <Route path="/personel/idari-personel" element={<AdministrativeStaff />} />
                    <Route path="/en/personel/idari-personel" element={<AdministrativeStaff />} />
                    <Route path="/personel/arastirma-gorevlisi" element={<ResearchStaff />} />
                    <Route path="/en/personel/arastirma-gorevlisi" element={<ResearchStaff />} />
                    <Route path="/personel/emekli-personel" element={<RetiredStaff />} />
                    <Route path="/en/personel/emekli-personel" element={<RetiredStaff />} />

                    <Route path="/belgeler/akademik-personel" element={<AcademicStaff />} />
                    <Route path="/en/belgeler/akademik-personel" element={<AcademicStaff />} />
                    <Route path="/belgeler/ogrenciler" element={<Students />} />
                    <Route path="/en/belgeler/ogrenciler" element={<Students />} />
                    <Route path="/belgeler/kanun-yonetmelikler" element={<Laws />} />
                    <Route path="/en/belgeler/kanun-yonetmelikler" element={<Laws />} />
                    <Route path="/belgeler/akademik-idari-personel" element={<AcademicStaff />} />
                    <Route path="/en/belgeler/akademik-idari-personel" element={<AcademicStaff />} />
                    <Route path="/belgeler/ogrenci" element={<Students />} />
                    <Route path="/en/belgeler/ogrenci" element={<Students />} />
                    <Route path="/belgeler/gerekli-belge-formlar" element={<PageTemplate title="Gerekli Belge ve Formlar"><div className="text-center text-gray-600">Bu sayfa yakında güncellenecektir.</div></PageTemplate>} />
                    <Route path="/en/belgeler/gerekli-belge-formlar" element={<PageTemplate title="Required Documents and Forms"><div className="text-center text-gray-600">This page will be updated soon.</div></PageTemplate>} />

                    <Route path="/kararlar/fakulte-kurulu" element={<FacultyBoardDecisions />} />
                    <Route path="/en/kararlar/fakulte-kurulu" element={<FacultyBoardDecisions />} />
                    <Route path="/kararlar/fakulte-kurulu/:year" element={<FacultyBoardYearPage />} />
                    <Route path="/en/kararlar/fakulte-kurulu/:year" element={<FacultyBoardYearPage />} />
                    <Route path="/kararlar/fakulte-yonetim" element={<FacultyManagementBoardDecisions />} />
                    <Route path="/en/kararlar/fakulte-yonetim" element={<FacultyManagementBoardDecisions />} />
                    <Route path="/kararlar/fakulte-yonetim/:year" element={<FacultyManagementBoardYearPage />} />
                    <Route path="/en/kararlar/fakulte-yonetim/:year" element={<FacultyManagementBoardYearPage />} />

                    <Route path="/fakulte-personeli/ogretim-uyeleri" element={<FacultyMembers />} />
                    <Route path="/en/fakulte-personeli/ogretim-uyeleri" element={<FacultyMembers />} />
                    <Route path="/fakulte-personeli/ogretim-gorevlileri" element={<LecturingStaff />} />
                    <Route path="/en/fakulte-personeli/ogretim-gorevlileri" element={<LecturingStaff />} />
                    <Route path="/fakulte-personeli/arastirma-gorevlileri" element={<ResearchAssistants />} />
                    <Route path="/en/fakulte-personeli/arastirma-gorevlileri" element={<ResearchAssistants />} />
                    <Route path="/fakulte-personeli/idari-personel" element={<AdministrativeStaff />} />
                    <Route path="/en/fakulte-personeli/idari-personel" element={<AdministrativeStaff />} />
                    <Route path="/fakulte-personeli/emekli-ogretim-elemanlari" element={<RetiredFaculty />} />
                    <Route path="/en/fakulte-personeli/emekli-ogretim-elemanlari" element={<RetiredFaculty />} />

                    <Route path="/arastirma/laboratuvarlar" element={<ResearchLabs />} />
                    <Route path="/en/arastirma/laboratuvarlar" element={<ResearchLabs />} />
                    <Route path="/arastirma/kitaplar-dergiler" element={<BooksAndJournals />} />
                    <Route path="/en/arastirma/kitaplar-dergiler" element={<BooksAndJournals />} />
                    <Route path="/arastirma/kitaplar-editorlukler" element={<BooksAndJournals />} />
                    <Route path="/en/arastirma/kitaplar-editorlukler" element={<BooksAndJournals />} />
                    <Route path="/arastirma/dergiler" element={<DergilerPage />} />
                    <Route path="/en/arastirma/dergiler" element={<DergilerPage />} />
                    <Route path="/arastirma/muzeler" element={<Museums />} />
                    <Route path="/en/arastirma/muzeler" element={<Museums />} />
                    <Route path="/arastirma/devam-eden-projelerimiz" element={<DevamEdenProjeler />} />
                    <Route path="/en/arastirma/devam-eden-projelerimiz" element={<DevamEdenProjeler />} />
                    <Route path="/arastirma/devam-eden-projelerimiz/dis-destekli" element={<DisDestekliProjeler />} />
                    <Route path="/en/arastirma/devam-eden-projelerimiz/dis-destekli" element={<DisDestekliProjeler />} />
                    <Route path="/arastirma/devam-eden-projelerimiz/bap-destekli" element={<BAPDestekliProjeler />} />
                    <Route path="/en/arastirma/devam-eden-projelerimiz/bap-destekli" element={<BAPDestekliProjeler />} />

                    <Route path="/bolumler/biyoloji/*" element={<Biology />} />
                    <Route path="/en/bolumler/biyoloji/*" element={<Biology />} />
                    <Route path="/bolumler/biyoloji/ogretim-uyeleri" element={<BiologyFaculty />} />
                    <Route path="/en/bolumler/biyoloji/ogretim-uyeleri" element={<BiologyFaculty />} />
                    <Route path="/bolumler/matematik" element={<Mathematics />} />
                    <Route path="/en/bolumler/matematik" element={<Mathematics />} />
                    <Route path="/bolumler/istatistik" element={<Statistics />} />
                    <Route path="/en/bolumler/istatistik" element={<Statistics />} />
                    <Route path="/bolumler/aktuerya-bilimleri" element={<ActuarialScience />} />
                    <Route path="/en/bolumler/aktuerya-bilimleri" element={<ActuarialScience />} />
                    <Route path="/bolumler/kimya-bolumu-sanal-tur" element={<KimyaBolumuSanalTur />} />
                    <Route path="/en/bolumler/kimya-bolumu-sanal-tur" element={<KimyaBolumuSanalTur />} />

                    <Route path="/mezunlar" element={<Graduates />} />
                    <Route path="/en/mezunlar" element={<Graduates />} />
                    <Route path="/mezunlar/liste" element={<Graduates />} />
                    <Route path="/en/mezunlar/liste" element={<Graduates />} />
                    <Route path="/mezunlar/basari-hikayeleri" element={<PageTemplate title="Başarı Hikayeleri"><div className="text-center text-gray-600">Bu sayfa yakında güncellenecektir.</div></PageTemplate>} />
                    <Route path="/en/mezunlar/basari-hikayeleri" element={<PageTemplate title="Success Stories"><div className="text-center text-gray-600">This page will be updated soon.</div></PageTemplate>} />
                    <Route path="/mezunlar/iletisim" element={<PageTemplate title="Mezun İletişim"><div className="text-center text-gray-600">Bu sayfa yakında güncellenecektir.</div></PageTemplate>} />
                    <Route path="/en/mezunlar/iletisim" element={<PageTemplate title="Alumni Contact"><div className="text-center text-gray-600">This page will be updated soon.</div></PageTemplate>} />
                    <Route path="/yuksek-lisans" element={<Graduates />} />
                    <Route path="/en/yuksek-lisans" element={<Graduates />} />
                    <Route path="/doktora" element={<Graduates />} />
                    <Route path="/en/doktora" element={<Graduates />} />
                    <Route path="/alumni" element={<AlumniPage />} />
                    <Route path="/en/alumni" element={<AlumniPage />} />

                    <Route path="/sayfa/:slug" element={<DynamicPage />} />
                    <Route path="/en/sayfa/:slug" element={<DynamicPage />} />

                    <Route path="/ogrenci-topluluklari" element={<OgrenciTopluluklari />} />
                    <Route path="/en/ogrenci-topluluklari" element={<OgrenciTopluluklari />} />
                    <Route path="/ogrenci-toplulukları-old" element={<StudentClubs />} />
                    <Route path="/en/ogrenci-toplulukları-old" element={<StudentClubs />} />

                    <Route path="/programlar/lisans" element={<UndergraduateProgramsPage />} />
                    <Route path="/en/programlar/lisans" element={<UndergraduateProgramsPage />} />
                    <Route path="/programlar/erasmus" element={<ErasmusPage />} />
                    <Route path="/en/programlar/erasmus" element={<ErasmusPage />} />
                    <Route path="/programlar/erasmus/ogrenci-deneyimleri" element={<ErasmusOgrenciDeneyimleri />} />
                    <Route path="/en/programlar/erasmus/ogrenci-deneyimleri" element={<ErasmusOgrenciDeneyimleri />} />
                    <Route path="/programlar/yandal" element={<YandalPage />} />
                    <Route path="/en/programlar/yandal" element={<YandalPage />} />
                    <Route path="/programlar/cift-anadal" element={<CiftAnadalPage />} />
                    <Route path="/en/programlar/cift-anadal" element={<CiftAnadalPage />} />
                    <Route path="/programlar/yuksek-lisans" element={<YuksekLisansPage />} />
                    <Route path="/en/programlar/yuksek-lisans" element={<YuksekLisansPage />} />
                    <Route path="/programlar/doktora" element={<DoktoraPage />} />
                    <Route path="/en/programlar/doktora" element={<DoktoraPage />} />

                    <Route path="/staj" element={<StajPage />} />
                    <Route path="/en/staj" element={<StajPage />} />

                    <Route path="/ogrenci/uluslararasi-ogrenciler" element={<UluslararasiOgrenciler />} />
                    <Route path="/en/student/international-students" element={<UluslararasiOgrenciler />} />

                    <Route path="/sosyal-sorumluluk-projeleri" element={<SosyalSorumlulukProjeleri />} />
                    <Route path="/en/sosyal-sorumluluk-projeleri" element={<SosyalSorumlulukProjeleri />} />

                    <Route path="/birim-faaliyetleri" element={<BirimFaaliyetleri />} />
                    <Route path="/en/birim-faaliyetleri" element={<BirimFaaliyetleri />} />
                    <Route path="/akademik-faaliyet" element={<AkademikFaaliyet />} />
                    <Route path="/en/akademik-faaliyet" element={<AkademikFaaliyet />} />
                    </Routes>
                    </Suspense>
                </main>
                <Footer />
            </div>
        </LanguageWrapper>
    );
}

export default App; 